import { BillingModel, BillingModelConfig, BillingModelFeatures } from './types';
import { BILLING_MODEL_CONFIGS, DEFAULT_BILLING_MODEL } from './configs';

/**
 * Get the configuration for a billing model.
 * Falls back to default (packages) if invalid model provided.
 */
export function getBillingModelConfig(model: BillingModel | undefined | null): BillingModelConfig {
  if (!model || !(model in BILLING_MODEL_CONFIGS)) {
    return BILLING_MODEL_CONFIGS[DEFAULT_BILLING_MODEL];
  }
  return BILLING_MODEL_CONFIGS[model];
}

/**
 * Check if a specific feature should be shown for a billing model.
 */
export function shouldShowFeature(
  model: BillingModel | undefined | null,
  feature: keyof BillingModelFeatures
): boolean {
  const config = getBillingModelConfig(model);
  return config.features[feature];
}

/**
 * Check if package-related UI should be shown.
 */
export function showsPackages(model: BillingModel | undefined | null): boolean {
  return shouldShowFeature(model, 'showPackages');
}

/**
 * Check if sessions remaining should be shown.
 */
export function showsSessionsRemaining(model: BillingModel | undefined | null): boolean {
  return shouldShowFeature(model, 'showSessionsRemaining');
}

/**
 * Check if reup projections should be shown.
 */
export function showsReupProjections(model: BillingModel | undefined | null): boolean {
  return shouldShowFeature(model, 'showReupProjections');
}

/**
 * Check if earnings per session should be tracked.
 */
export function tracksEarningsPerSession(model: BillingModel | undefined | null): boolean {
  return shouldShowFeature(model, 'trackEarningsPerSession');
}

/**
 * Check if package is required to book a session.
 */
export function requiresPackageForSession(model: BillingModel | undefined | null): boolean {
  return shouldShowFeature(model, 'requirePackageForSession');
}

/**
 * Get the display label for a billing model.
 */
export function getBillingModelLabel(model: BillingModel | undefined | null): string {
  return getBillingModelConfig(model).label;
}

/**
 * Get the description for a billing model.
 */
export function getBillingModelDescription(model: BillingModel | undefined | null): string {
  return getBillingModelConfig(model).description;
}
